import { Fragment, useMemo } from "react";
import { ChevronRight, Search, X } from "lucide-react";
import { PILLAR_BLURB, pillarKey } from "@/lib/utils/daylight-insight/pillars";
import { fmtVal } from "@/lib/utils/daylight-insight/format";
import type { Metric } from "@/lib/utils/daylight-insight/types";
import type { CustomViewType } from "@/components/daylight-insight/api/types";
import AllMetricsPeriodSkeleton from "./AllMetricsPeriodSkeleton";

interface IntervalMetric {
  metric: Metric;
  values: (number | null)[];
  /** From the interval SP's `HasDrillDown`: true → this metric has underlying
   *  records, so the row offers "Records"; false → "Details". */
  hasDrillDown: boolean;
}

interface IntervalGroup {
  category: string;
  metrics: IntervalMetric[];
}

const StateMsg = ({ children }: { children: React.ReactNode }) => (
  <div style={{ padding: 32, textAlign: "center", color: "var(--ink-3)", fontSize: 13 }}>{children}</div>
);

/** By Month / Week / Day grid for All Metrics (read-only table controls). */
export default function AllMetricsPeriodTable({
  pivot,
  prefix,
  isoPeriods,
  periodLabels,
  groups,
  loading,
  error,
  query,
  setQuery,
  pillarFilter,
  onPillarChange,
  allGroups,
  onOpenRecords,
  onOpenDetails,
}: {
  pivot: CustomViewType;
  prefix: string;
  isoPeriods: string[];
  periodLabels: string[];
  groups: IntervalGroup[];
  loading: boolean;
  error: string | null;
  query: string;
  setQuery: (v: string) => void;
  pillarFilter: string;
  onPillarChange: (cat: string) => void;
  allGroups: IntervalGroup[];
  /** `HasDrillDown = 1` rows: open the Drill Down modal on the records behind ONE
   *  period's value. `periodIso` is the clicked column's interval start — the
   *  modal fetches that period alone, not the whole trailing window. */
  onOpenRecords: (im: IntervalMetric, category: string, periodIso: string) => void;
  /** `HasDrillDown = 0` rows: open the metric's trends drill — the same view the
   *  Operating View opens when a metric is clicked. */
  onOpenDetails: (im: IntervalMetric, category: string) => void;
}) {
  const visibleIsos = isoPeriods;
  const visibleLabels = periodLabels;
  const isoIndex = useMemo(() => new Map(isoPeriods.map((iso, i) => [iso, i])), [isoPeriods]);

  return (
    <>
      <div className="am-period-head">
        <span className="am-period-prefix" style={{ margin: 0 }}>{prefix}</span>
        <div className="tbl-search">
          <Search size={14} />
          <input placeholder="Find a metric…" value={query} onChange={(e) => setQuery(e.target.value)} />
          {query && <button onClick={() => setQuery("")} aria-label="clear"><X size={13} /></button>}
        </div>
      </div>
      {!loading && allGroups.length > 0 && (
        <div className="am-pill-chips">
          <button className={"am-pill-chip" + (pillarFilter === "all" ? " on" : "")} onClick={() => onPillarChange("all")}>
            All pillars <span className="cnt">{allGroups.reduce((n, g) => n + g.metrics.length, 0)}</span>
          </button>
          {allGroups.map((g) => (
            <button key={g.category} className={"am-pill-chip" + (pillarFilter === g.category ? " on" : "")} onClick={() => onPillarChange(g.category)}>
              {g.category.split(" · ")[0]} <span className="cnt">{g.metrics.length}</span>
            </button>
          ))}
        </div>
      )}
      <div style={{ border: "1px solid var(--line)", borderRadius: 14, overflow: "hidden", background: "#fff", boxShadow: "var(--shadow-card)" }}>
        {loading ? (
          <AllMetricsPeriodSkeleton cols={visibleIsos.length || periodLabels.length || 12} />
        ) : error ? (
          <StateMsg>{error}</StateMsg>
        ) : !groups.length ? (
          <StateMsg>No metrics for this selection.</StateMsg>
        ) : (
          <table className="dl-tbl am-grid-period" style={{ tableLayout: "fixed" }}>
            <thead>
              <tr>
                <th style={{ width: "22%" }}>Metric</th>
                {visibleLabels.map((p, i) => (
                  <th key={visibleIsos[i] || i} style={{ textAlign: "right", width: `${78 / Math.max(visibleLabels.length, 1)}%`, fontSize: 8 }}>{p}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <Fragment key={g.category}>
                  <tr className="am-group-head">
                    <td colSpan={1 + visibleIsos.length}>
                      <div className="am-group-inner">
                        <span className="am-group-accent" />
                        <span className="am-group-name">{g.category.split(" · ")[0]}</span>
                        <span className="am-group-count">{g.metrics.length}</span>
                        <span className="am-group-blurb">{PILLAR_BLURB[pillarKey(g.category)] || ""}</span>
                      </div>
                    </td>
                  </tr>
                  {g.metrics.map((im) => (
                    // The affordance follows `HasDrillDown`, and so does WHERE you
                    // click. Records are per-period, so each value is its own
                    // target and the row itself does nothing — a whole-row click
                    // couldn't say which column's records you meant. Metrics with
                    // no records keep the row click: their trends drill spans the
                    // full window, so no single column owns it.
                    <tr
                      key={im.metric.id}
                      className={"am-mrow" + (im.hasDrillDown ? " am-mrow-vals" : "")}
                      onClick={im.hasDrillDown ? undefined : () => onOpenDetails(im, g.category)}
                      title={im.hasDrillDown ? undefined : "Open metric detail"}
                    >
                      <td>
                        <div className="am-metric-name-cell">
                          <span className="am-branch-name" title={im.metric.name}>{im.metric.name}</span>
                          <span className={"am-row-hint" + (im.hasDrillDown ? " rec" : "")}>
                            {im.hasDrillDown ? "Records" : "Details"}<ChevronRight size={13} />
                          </span>
                        </div>
                      </td>
                      {visibleIsos.map((iso, i) => {
                        const idx = isoIndex.get(iso) ?? -1;
                        const v = idx >= 0 ? im.values[idx] : null;
                        // No records behind an empty cell — leave the em dash inert
                        // rather than opening a modal that can only say "none".
                        const drillable = im.hasDrillDown && v != null;
                        return (
                          <td key={iso} className="num" style={{ textAlign: "right", fontSize: 11 }}>
                            {v == null ? (
                              "—"
                            ) : drillable ? (
                              <button
                                type="button"
                                className="am-val-btn"
                                onClick={() => onOpenRecords(im, g.category, iso)}
                                title={`View ${im.metric.name} records for ${visibleLabels[i] ?? ""}`}
                              >
                                {fmtVal(im.metric, v)}
                              </button>
                            ) : (
                              fmtVal(im.metric, v)
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
